import { VideoRecorderResult } from '@webcore/ui/hooks/useVideoRecorder';

interface RecordButtonProps {
  recorder: VideoRecorderResult;
  className?: string;
}

/**
 * RecordButton component that works with the useVideoRecorder hook
 */
const RecordButton = ({ recorder, className = '' }: RecordButtonProps) => {
  const { state, progress, startRecording, stopRecording } = recorder;
  
  const isRecording = state === 'recording';
  const isProcessing = state === 'processing';
  
  const handleClick = () => {
    if (isProcessing) {
      return; // Prevent clicks during processing
    }
    
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div 
      className={`relative w-[100px] h-[100px] transition-opacity duration-500 ease-in-out pointer-events-auto ${className}`}
      data-recording-state={state}
    >
      <svg
        viewBox="0 0 38 38"
        className={`block absolute top-0 left-0 w-full h-full scale-100 ${
          isRecording ? 'opacity-100' : 'transition-all duration-300'
        }`}
      >
        {/* Outer circle track */}
        <circle
          className={`fill-[rgba(0,0,0,0.2)] stroke-[1px] stroke-white transition-all duration-700 `}
          r="16"
          cx="19"
          cy="19"
        ></circle>
        
        {/* Progress bar */}
        <circle
          className={`fill-transparent stroke-[6px] transition-none origin-center stroke-[rgba(255,52,52,0.5)] rotate-[-90deg] scale-[0.67] ${
            isRecording ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            strokeDasharray: '100.5 100.5', // Use a slightly larger circumference value
            strokeDashoffset: isRecording ? `${100 - (progress * 100)}` : '100'
          }}
          r="16"
          cx="19"
          cy="19"
        ></circle>
        
        {/* Processing spinner between inner and outer circles */}
        {isProcessing && (
          <g className="animate-spin" style={{ transformOrigin: '19px 19px' }}>
            <circle
              className="fill-transparent"
              style={{
                stroke: '#FF3434',
                strokeWidth: '1px',
                strokeDasharray: '20 45',
                r: '13',
                cx: '19',
                cy: '19'
              }}
            />
          </g>
        )}
      </svg>

      {/* Inner button */}
      <button
        className={`absolute top-0 left-0 origin-center bg-[#FF3434] transition-all duration-300 w-full h-full rounded-full text-transparent ${
          isRecording ? 'scale-[0.48]' : 'scale-[0.35]'
        }`}
        onClick={handleClick}
        disabled={isProcessing}
        aria-label={isRecording ? "Stop recording" : isProcessing ? "Processing video" : "Start recording"}
      >
      </button>
    </div>
  );
};

export default RecordButton;